import { SectionHeader } from '@/components/ui';
import { useTranslate } from '@/i18n/LocaleLanguage';
import { cleanDescription } from '@/seo/text';

export interface AboutSectionProps {
  /** The entity's cleaned name: "About {name}". */
  name: string;
  /** Staff "About" text (backend B3.4 `description`): plain text, line breaks kept. */
  description?: string | null;
  className?: string;
}

/**
 * "About {Brand}" (web plan W3.6; also a shop's and a store's): the backend's
 * staff-written `description` as plain text, paragraphs kept, through the
 * same `cleanDescription` as every visible description (styled letters folded,
 * emoji, URLs and hashtags out) and the JSON-LD's `description`. React renders
 * it as text, never as HTML. Nothing at all when there is no text.
 */
export function AboutSection({ name, description, className }: AboutSectionProps) {
  const t = useTranslate();
  const text = cleanDescription(description, { max: Infinity, keepLineBreaks: true });
  if (!text || !name) return null;
  const title = t('common:about', { name });
  return (
    <section aria-label={title} className={className}>
      <SectionHeader title={title} wrap />
      <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-ink-secondary">{text}</p>
    </section>
  );
}

export default AboutSection;
